import React, { useEffect, useState, useCallback } from 'react';
import { SectionTitle } from './PageShell';
import AdminShell from './AdminShell';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  Mail, MailOpen, Trash2, Loader2, Check, Archive, Inbox, Paperclip, CornerUpLeft, RotateCcw,
} from 'lucide-react';

const FOLDERS = [
  { v: '', label: 'Reçus', Icon: Inbox },
  { v: 'unread', label: 'Non lus', Icon: Mail },
  { v: 'archived', label: 'Archivés', Icon: Archive },
];

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function MailBody({ mail }) {
  if (mail.html) {
    return (
      <iframe
        title={`mail-${mail.id}`}
        sandbox=""
        srcDoc={mail.html}
        className="w-full min-h-[260px] rounded-lg bg-white"
        data-testid={`inbox-mail-html-${mail.id}`}
      />
    );
  }
  return (
    <div className="text-white/80 text-[13.5px] leading-relaxed whitespace-pre-wrap" data-testid={`inbox-mail-text-${mail.id}`}>
      {mail.text || <span className="text-white/40 italic">Corps du message indisponible.</span>}
    </div>
  );
}

export default function AdminInboxPage() {
  const { user, loading: authLoading } = useAuth();
  const [folder, setFolder] = useState('');
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [replyForId, setReplyForId] = useState(null);
  const [replySubject, setReplySubject] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyResult, setReplyResult] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.adminInboxList(folder || undefined);
      setItems(d.items || []);
      setUnread(d.unread || 0);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, [folder]);

  useEffect(() => { if (!authLoading && user?.is_admin) refresh(); }, [authLoading, user, refresh]);

  const openMail = async (m) => {
    setOpenId(openId === m.id ? null : m.id);
    if (!m.read) {
      try { await api.adminInboxUpdate(m.id, { read: true }); } catch (_) { /* silent */ }
      setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
  };

  const startReply = (m) => {
    setReplyForId(m.id);
    const subj = m.subject || '';
    setReplySubject(subj.toLowerCase().startsWith('re') ? subj : `Re : ${subj}`);
    setReplyMessage(`Bonjour${m.from_name ? ' ' + m.from_name.split(' ')[0] : ''},\n\n\n\nÀ bientôt sur la route,\nBruno — Code Voiture\ncontact@codevoiture.fr`);
    setReplyResult(null);
  };

  const sendReply = async (id) => {
    setReplySending(true);
    setReplyResult(null);
    try {
      const r = await api.adminInboxReply(id, { subject: replySubject, message: replyMessage });
      setReplyResult({ id, ok: r.ok, error: r.delivery?.error });
      if (r.ok) { setReplyForId(null); await refresh(); }
    } catch (e) {
      setReplyResult({ id, ok: false, error: e?.response?.data?.detail || "Erreur d'envoi" });
    }
    setReplySending(false);
  };

  const toggleArchive = async (m) => {
    try { await api.adminInboxUpdate(m.id, { archived: !m.archived }); await refresh(); } catch (_) { /* silent */ }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer définitivement cet email ?')) return;
    try { await api.adminInboxDelete(id); await refresh(); } catch (_) { /* silent */ }
  };

  const downloadAtt = async (m, a) => {
    try {
      const blob = await api.adminInboxAttachment(m.id, a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = a.filename || 'piece-jointe';
      link.click();
      URL.revokeObjectURL(url);
    } catch (_) { window.alert('Téléchargement impossible.'); }
  };

  if (authLoading) return <AdminShell><div className="max-w-6xl mx-auto p-12 text-white/50">Chargement…</div></AdminShell>;
  if (!user?.is_admin) return <AdminShell><div className="max-w-3xl mx-auto p-12 text-center text-white/60">Accès réservé aux administrateurs.</div></AdminShell>;

  return (
    <AdminShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8" data-testid="admin-inbox-page">
        <SectionTitle
          kicker="Admin · Messagerie"
          title="Boîte mail — contact@codevoiture.fr"
          sub="Les emails reçus sur l'adresse officielle. Répondez directement depuis le back-office."
        />

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {FOLDERS.map((f) => (
            <button
              key={f.v}
              onClick={() => { setFolder(f.v); setOpenId(null); }}
              data-testid={`admin-inbox-folder-${f.v || 'all'}`}
              className={`px-4 h-9 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5 border ${folder === f.v ? 'bg-white text-black border-white' : 'bg-white/[0.03] text-white/55 hover:text-white border-white/10'}`}
            >
              <f.Icon size={12} /> {f.label}
              {f.v === 'unread' && unread > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold inline-flex items-center justify-center">{unread}</span>}
            </button>
          ))}
          <Link to="/admin" className="ml-auto text-[13px] text-white/60 hover:text-white">← Dashboard admin</Link>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="admin-inbox-empty">
            <Inbox size={28} className="mx-auto text-white/25 mb-3" />
            <div className="text-white/70 text-[14.5px] font-semibold">Aucun email {folder === 'archived' ? 'archivé' : folder === 'unread' ? 'non lu' : 'reçu'}</div>
            <p className="text-white/40 text-[12.5px] mt-2 max-w-md mx-auto">
              Les emails envoyés à contact@codevoiture.fr apparaîtront ici dès que la réception Resend (enregistrement MX + webhook) sera activée sur le domaine.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {items.map((m) => {
              const opened = openId === m.id;
              return (
                <div key={m.id} className={`rounded-2xl border transition ${opened ? 'border-red-500/30 bg-white/[0.03]' : m.read ? 'border-white/8 bg-white/[0.015]' : 'border-white/15 bg-white/[0.04]'}`} data-testid={`admin-inbox-item-${m.id}`}>
                  <button onClick={() => openMail(m)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left" data-testid={`admin-inbox-open-${m.id}`}>
                    {m.read ? <MailOpen size={16} className="text-white/30 shrink-0" /> : <Mail size={16} className="text-red-400 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[13.5px] truncate ${m.read ? 'text-white/70' : 'text-white font-bold'}`}>{m.from_name || m.from_email}</span>
                        {m.attachments?.length > 0 && <Paperclip size={12} className="text-white/40 shrink-0" />}
                        {m.replies?.length > 0 && <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 shrink-0">Répondu</span>}
                      </div>
                      <div className={`text-[13px] truncate ${m.read ? 'text-white/45' : 'text-white/80'}`}>{m.subject}</div>
                    </div>
                    <span className="text-white/35 text-[11.5px] font-mono-spec shrink-0">{fmtDate(m.received_at)}</span>
                  </button>

                  {opened && (
                    <div className="px-4 pb-4 space-y-4 border-t border-white/8 pt-4">
                      <div className="text-white/45 text-[12px]">De : <span className="text-white/70">{m.from_email}</span></div>
                      <MailBody mail={m} />

                      {m.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {m.attachments.map((a) => (
                            <button key={a.id} onClick={() => downloadAtt(m, a)} data-testid={`admin-inbox-att-${a.id}`} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-white/15 bg-white/5 text-white/75 text-[12px] hover:border-white/35">
                              <Paperclip size={11} /> {a.filename}
                            </button>
                          ))}
                        </div>
                      )}

                      {m.replies?.length > 0 && (
                        <div className="space-y-2">
                          {m.replies.map((r, i) => (
                            <div key={i} className="rounded-xl border border-emerald-500/20 bg-emerald-950/10 p-3">
                              <div className="text-[10.5px] uppercase tracking-widest text-emerald-300 font-bold mb-1">Réponse envoyée · {fmtDate(r.sent_at)}</div>
                              <div className="text-white/70 text-[12.5px] whitespace-pre-wrap">{r.message}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {replyForId === m.id ? (
                        <div className="rounded-xl border border-red-500/25 bg-red-950/10 p-4 space-y-3" data-testid={`admin-inbox-reply-panel-${m.id}`}>
                          <div className="text-[10.5px] uppercase tracking-widest text-red-300 font-bold">Réponse à {m.from_email} · envoyée depuis contact@codevoiture.fr</div>
                          <input
                            value={replySubject}
                            onChange={(e) => setReplySubject(e.target.value)}
                            data-testid="admin-inbox-reply-subject"
                            className="w-full h-10 rounded-lg bg-black/40 border border-white/15 px-3 text-white text-[13.5px] focus:border-red-500 outline-none"
                            placeholder="Objet"
                          />
                          <textarea
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            data-testid="admin-inbox-reply-message"
                            rows={7}
                            className="w-full rounded-lg bg-black/40 border border-white/15 px-3 py-2.5 text-white text-[13.5px] leading-relaxed focus:border-red-500 outline-none resize-y"
                            placeholder="Votre réponse…"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => sendReply(m.id)}
                              disabled={replySending || replyMessage.trim().length < 5}
                              data-testid="admin-inbox-reply-send"
                              className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white text-[12.5px] font-semibold"
                            >
                              {replySending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Envoyer la réponse
                            </button>
                            <button onClick={() => setReplyForId(null)} className="px-4 h-9 rounded-full border border-white/15 text-white/60 text-[12.5px] hover:text-white">Annuler</button>
                          </div>
                          {replyResult && replyResult.id === m.id && !replyResult.ok && (
                            <div className="text-red-300 text-[12.5px]" data-testid="admin-inbox-reply-error">⚠️ Échec d&apos;envoi : {String(replyResult.error || 'inconnu')}</div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => startReply(m)} data-testid={`admin-inbox-reply-btn-${m.id}`} className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-red-600 hover:bg-red-500 text-white text-[12.5px] font-semibold">
                            <CornerUpLeft size={13} /> Répondre depuis contact@codevoiture.fr
                          </button>
                          <button onClick={() => toggleArchive(m)} data-testid={`admin-inbox-archive-${m.id}`} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border border-white/15 text-white/65 text-[12.5px] hover:text-white">
                            {m.archived ? <><RotateCcw size={12} /> Restaurer</> : <><Archive size={12} /> Archiver</>}
                          </button>
                          <button onClick={() => remove(m.id)} data-testid={`admin-inbox-delete-${m.id}`} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border border-red-500/25 text-red-300/80 text-[12.5px] hover:text-red-200">
                            <Trash2 size={12} /> Supprimer
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </AdminShell>
  );
}
